<template>
  <v-navigation-drawer
    app
    permanent
    class="app-drawer"
    v-model="drawer"
    :width="drawerWidth"
    :mini-variant="mini"
    dark
  >
    <!-- Header: logo + hamburger collapse + map link -->
    <div class="drawer-header">
      <div class="drawer-logo" v-if="!mini">
        <img :src="logoUrl" height="36" alt="Kalan" />
      </div>
      <div class="drawer-header-actions" :class="{ 'is-mini': mini }">
        <button
          type="button"
          class="drawer-icon-btn"
          data-tip="Compactar menu"
          @click="handleDrawerCollapse"
        >
          <v-icon size="20" color="rgba(255,255,255,0.7)">mdi-menu</v-icon>
        </button>
        <button
          type="button"
          class="drawer-icon-btn"
          data-tip="Ir al mapa"
          @click="goToMap"
        >
          <v-icon size="20" color="rgba(255,255,255,0.7)">mdi-map-search</v-icon>
        </button>
      </div>
    </div>

    <!-- Menu -->
    <div class="drawer-menu">
      <v-list class="pa-0" dense>
        <template v-for="(item, key) in computeMenu">
          <template v-if="item.children && item.children.length > 0">
            <v-list-group
              :key="key"
              no-action
              :to="item.path"
              color="#9588F0"
              :prepend-icon="getMenuIcon(item)"
            >
              <template v-slot:activator>
                <v-list-item-content>
                  <v-list-item-title
                    v-text="$vuetify.lang.t('$vuetify.menu.' + item.meta.title)"
                  />
                </v-list-item-content>
              </template>
              <v-list-item
                :class="mini ? 'pl-4' : ''"
                v-for="subItem in item.children"
                :key="subItem.name"
                :to="subItem.path"
                v-show="!subItem.meta.hiddenInMenu"
                color="#9588F0"
              >
                <v-list-item-icon>
                  <v-tooltip right>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" size="18" color="rgba(255,255,255,0.7)">{{ getMenuIcon(subItem) }}</v-icon>
                    </template>
                    <span>{{ $vuetify.lang.t('$vuetify.menu.' + subItem.meta.title) }}</span>
                  </v-tooltip>
                </v-list-item-icon>
                <v-list-item-content v-if="!mini">
                  <v-list-item-title
                    v-text="$vuetify.lang.t('$vuetify.menu.' + subItem.meta.title)"
                  />
                </v-list-item-content>
              </v-list-item>
            </v-list-group>
          </template>
          <template v-else>
            <v-list-item
              :key="key"
              :to="item.path"
              v-if="validate(item)"
              v-show="!item.meta.hiddenInMenu"
              color="#9588F0"
            >
              <v-list-item-icon>
                <v-tooltip right>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" size="20" color="rgba(255,255,255,0.7)">{{ getMenuIcon(item) }}</v-icon>
                  </template>
                  <span>{{ $vuetify.lang.t('$vuetify.menu.' + item.meta.title) }}</span>
                </v-tooltip>
              </v-list-item-icon>
              <v-list-item-content v-if="!mini">
                <v-list-item-title
                  v-text="$vuetify.lang.t('$vuetify.menu.' + item.meta.title)"
                />
              </v-list-item-content>
            </v-list-item>
          </template>
        </template>
      </v-list>
    </div>

    <!-- Footer: user info + logout icon -->
    <template v-slot:append>
      <div class="drawer-footer" :class="{ 'is-mini': mini }">
        <div class="drawer-user-avatar">
          <v-icon size="18" color="rgba(255,255,255,0.7)">mdi-account</v-icon>
        </div>
        <div class="drawer-user-meta" v-if="!mini && getUser && getUser.attributes">
          <div class="drawer-user-name">
            {{ getUser.attributes.nombre }} {{ getUser.attributes.apaterno }}
          </div>
          <div class="drawer-user-role">{{ getUser.administrator ? 'Administrador' : 'Operador' }}</div>
        </div>
        <button
          v-if="!mini"
          type="button"
          class="drawer-logout-icon"
          data-tip="Cerrar sesion"
          @click="handleLogout"
        >
          <v-icon size="18" color="rgba(255,255,255,0.7)">mdi-logout</v-icon>
        </button>
      </div>
    </template>
  </v-navigation-drawer>
</template>
<script>
import { mapGetters } from 'vuex';
import { protectedRoute as routes } from '@/router/routes';
import logoUrl from '@/assets/logo.svg';

const ICON_MAP = {
  home: 'mdi-view-dashboard',
  users: 'mdi-account-multiple',
  profiles: 'mdi-card-account-details',
  groups: 'mdi-account-group',
  cameras: 'mdi-cctv',
  bus: 'mdi-bus',
  geofences: 'mdi-map-marker-radius',
  'mando-unico': 'mdi-shield-star',
  'mando-unico-geofences': 'mdi-map-marker-radius',
  'mando-unico-region': 'mdi-map-outline',
  'mando-unico-sector': 'mdi-vector-square',
  'mando-unico-cuadrante': 'mdi-grid',
  'lpr-categories': 'mdi-folder-image',
  lpr: 'mdi-camera-front',
  'sensor-categories': 'mdi-folder-alert',
  sensor: 'mdi-alarm-light',
  drivers: 'mdi-account-tie',
  deviceCategories: 'mdi-folder-multiple',
  devices: 'mdi-car-emergency',
  fuelPrice: 'mdi-fuel',
  'video-servers': 'mdi-server-network',
  routes: 'mdi-map-marker-path',
  reports: 'mdi-chart-line',
  'incident-categories': 'mdi-shield-alert'
};

export default {
  name: 'AppDrawer',
  components: {},
  props: {
    expanded: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      logoUrl,
      mini: false,
      drawerWidth: 256,
      drawer: true
    };
  },
  computed: {
    ...mapGetters('auth', ['getUser']),
    computeMenu() {
      return this.filterMenu(routes[0].children);
    }
  },
  methods: {
    // Filtra el menú por permisos, incluyendo GRUPOS (el template no aplica validate en la rama
    // de v-list-group): un grupo solo aparece si el usuario pasa su validate Y le queda al menos
    // un hijo visible. Sin esto, un grupo con validate (p.ej. Listas negras) se mostraría a
    // usuarios sin el permiso.
    filterMenu(items) {
      return (items || []).reduce((acc, item) => {
        if (!this.validate(item)) return acc;
        if (item.children && item.children.length > 0) {
          const children = this.filterMenu(item.children);
          if (children.length > 0) acc.push({ ...item, children });
        } else {
          acc.push(item);
        }
        return acc;
      }, []);
    },
    getMenuIcon(item) {
      return ICON_MAP[item.name] || item.meta.icon || 'mdi-circle-small';
    },
    handleDrawerCollapse() {
      this.mini = !this.mini;
    },
    toggleDrawer() {
      this.drawer = !this.drawer;
    },
    goToMap() {
      if (this.$route.name === 'map') return
      // .catch silencia el error "Navigation cancelled" que Vue Router 3
      // arroja cuando el operador hace doble click rapido (la segunda
      // navegacion cancela la primera; la final completa OK).
      this.$router.push({ name: 'map' }).catch(function() {})
    },
    async handleLogout() {
      try {
        await this.$confirm({
          title: 'Cerrar sesión',
          description: '¿Está seguro de que desea cerrar la sesión?',
          acceptText: 'Sí, cerrar sesión',
          cancelText: 'Cancelar',
        });
        this.$store.dispatch('auth/logout');
      } catch (e) {
        // cancelado
      }
    },
    validate(route) {
      if (this.getUser.administrator) {
        return true;
      }
      if (route.meta.admin) {
        return false;
      }
      if (!route.meta.validate) {
        return true;
      }
      if (route.meta.validate(this.getUser)) {
        return true;
      }
      return false;
    }
  },
  watch: {
  '$vuetify.breakpoint.mdAndDown'(isSmall) {
    this.mini = isSmall;
  }
},
mounted() {
  if (this.$vuetify.breakpoint.mdAndDown) {
    this.mini = true;
  }
}
};

</script>

<style lang="scss" scoped>
.app-drawer {
  overflow: hidden !important;
  background: #0D1121 !important;
  border-right: 1px solid rgba(255, 255, 255, 0.06);
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  min-height: 60px;
  gap: 8px;
}

.drawer-logo {
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1;
}

.drawer-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;

  &.is-mini {
    flex-direction: column;
    width: 100%;
    align-items: center;
  }
}

.drawer-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: transparent;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  padding: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.1);
  }
}

.drawer-menu {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.drawer-menu ::v-deep .v-list-group__header .v-list-item__icon .v-icon {
  color: rgba(255, 255, 255, 0.7) !important;
  font-size: 20px !important;
}


.drawer-footer {
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 10px;

  &.is-mini {
    justify-content: center;
    padding: 12px 0;
  }
}

.drawer-user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(124, 109, 240, 0.15);
  border: 1px solid rgba(124, 109, 240, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.drawer-user-meta {
  min-width: 0;
  flex: 1;
}

.drawer-user-name {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.drawer-user-role {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-top: 1px;
}

.drawer-header-actions .drawer-icon-btn[data-tip]::after {
  top: 110% !important;   
}

.drawer-footer .drawer-logout-icon[data-tip]::after {
  transform: translateX(-70%) !important; 
}

.drawer-logout-icon {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: transparent;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, border-color 0.15s;
  padding: 0;

  &:hover {
    background: rgba(176, 80, 80, 0.12);
    border-color: rgba(176, 80, 80, 0.3);

    .v-icon {
      color: #D17070 !important;
    }
  }
}
</style>
